import { SETTING_KEYS } from './snapshot';

// Unsaved-changes diff (build plan §5.5). Pure.
//
// Values are compared key-order-insensitively: Postgres jsonb reorders
// object keys, so a field whose keys merely come back in another order is
// never an unsaved change.

const stable = (value) => {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        out[key] = stable(value[key]);
      });
    return out;
  }
  return value;
};

export const sig = (value) => JSON.stringify(stable(value));

const byId = (fields) => {
  const map = new Map();
  fields.forEach((f) => map.set(f.id, f));
  return map;
};

// → { total, settingsChanged: [keys], added: [ids], changed: [ids],
//     removed: [ids], orderChanged }
export const computeChanges = (saved, working) => {
  const settingsChanged = SETTING_KEYS.filter(
    (key) => sig(saved[key]) !== sig(working[key])
  );
  const before = byId(saved.fields);
  const after = byId(working.fields);
  const added = working.fields
    .filter((f) => !before.has(f.id))
    .map((f) => f.id);
  const changed = working.fields
    .filter((f) => before.has(f.id) && sig(before.get(f.id)) !== sig(f))
    .map((f) => f.id);
  const removed = saved.fields.filter((f) => !after.has(f.id)).map((f) => f.id);
  const savedOrder = saved.fields
    .filter((f) => after.has(f.id))
    .map((f) => f.id);
  const workingOrder = working.fields
    .filter((f) => before.has(f.id))
    .map((f) => f.id);
  const orderChanged = savedOrder.join('|') !== workingOrder.join('|');
  const total =
    added.length +
    changed.length +
    removed.length +
    settingsChanged.length +
    (orderChanged ? 1 : 0);
  return { total, settingsChanged, added, changed, removed, orderChanged };
};

export const isDirty = (changes) => !!changes && changes.total > 0;

export const MAX_SECTION_PILLS = 4;

// Sections (in working order) holding added or changed fields. Fields
// before the first section belong to "Start".
const touchedSections = (working, touched) => {
  const groups = [];
  let current = { key: 'start', label: 'Start', fieldId: null, count: 0 };
  working.fields.forEach((f) => {
    if (f.type === 'section') {
      const label = (f.label || '').trim() || 'Untitled section';
      current = { key: f.id, label, fieldId: null, count: 0 };
    }
    if (!touched.has(f.id)) return;
    if (!current.count) {
      current.fieldId = f.id;
      groups.push(current);
    }
    current.count += 1;
  });
  return groups;
};

// SaveBar pills: up to 4 section pills, "+N more", "Details" (settings or
// order) and "Removed". { key, label, count?, static?, fieldId }.
export const changePills = (saved, working, changes) => {
  const c = changes || computeChanges(saved, working);
  const touched = new Set([...c.added, ...c.changed]);
  const groups = touchedSections(working, touched);
  const pills = groups.slice(0, MAX_SECTION_PILLS).map((g) => ({
    key: `section:${g.key}`,
    label: g.label,
    count: g.count,
    fieldId: g.fieldId,
  }));
  if (groups.length > MAX_SECTION_PILLS) {
    pills.push({
      key: 'more',
      label: `+${groups.length - MAX_SECTION_PILLS} more`,
      static: true,
      fieldId: null,
    });
  }
  const details = c.settingsChanged.length + (c.orderChanged ? 1 : 0);
  if (details) {
    pills.push({
      key: 'details',
      label: 'Details',
      count: details,
      fieldId: null,
    });
  }
  if (c.removed.length) {
    pills.push({
      key: 'removed',
      label: 'Removed',
      count: c.removed.length,
      static: true,
      fieldId: null,
    });
  }
  return pills;
};
